// DESCRIPTION: Shared time-picker input used across admin forms and wizards. Thin
// wrapper around <input type="time"> with two visual variants and an optional label.

/**
 * Built-in visual variants.
 *
 * - default: Tailwind nb- utilities, used in admin forms and the event wizard.
 * - wizard:  matches .nemtly-wizard-time in src/css/setup-wizard.css.
 */
const VARIANT_CLASSES = {
	default:
		'nemtly-time-input nb-px-2 nb-py-1.5 nb-border nb-border-gray-300 nb-rounded-md nb-text-sm nb-bg-white',
	wizard: 'nemtly-wizard-time',
};

/**
 * TimeInput — shared HH:MM time picker.
 *
 * Wraps a native <input type="time"> so every admin surface renders the same
 * control with consistent styling. Returns the value as the HH:MM string the
 * browser emits, identical to what the underlying input would emit directly.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.value       HH:MM (24h) string.
 * @param {Function} props.onChange    Called with the new HH:MM string.
 * @param {string}   [props.label]     Optional visible label. When set, renders a label element above the input.
 * @param {string}   [props.ariaLabel] Accessible label when no visible label is shown.
 * @param {string}   [props.variant]   'default' | 'wizard'. Defaults to 'default'.
 * @param {string}   [props.className] Extra classes appended after the variant classes.
 * @param {boolean}  [props.required]  Native required flag.
 * @param {boolean}  [props.disabled]  Native disabled flag.
 * @param {string}   [props.id]        Input id (auto-linked to label when both are provided).
 * @param {string}   [props.name]      Input name attribute.
 * @param {string}   [props.help]      Optional helper text shown beneath the input.
 *
 * @return {JSX.Element}
 */
export default function TimeInput( {
	value,
	onChange,
	label,
	ariaLabel,
	variant = 'default',
	className = '',
	required = false,
	disabled = false,
	id,
	name,
	help,
} ) {
	const variantClass = VARIANT_CLASSES[ variant ] || VARIANT_CLASSES.default;
	const inputClass = className
		? `${ variantClass } ${ className }`
		: variantClass;
	const inputId =
		id ||
		( label
			? `nemtly-time-${ Math.random().toString( 36 ).slice( 2, 9 ) }`
			: undefined );

	const input = (
		<input
			type="time"
			id={ inputId }
			name={ name }
			value={ value || '' }
			onChange={ ( e ) => onChange( e.target.value ) }
			className={ inputClass }
			aria-label={ ! label ? ariaLabel : undefined }
			required={ required }
			disabled={ disabled }
		/>
	);

	if ( ! label ) {
		return input;
	}

	return (
		<div className="nemtly-time-input-field">
			<label
				htmlFor={ inputId }
				className="nb-block nb-text-sm nb-font-semibold nb-text-gray-700 nb-mb-1"
			>
				{ label }
				{ required && <span className="nb-text-red-500"> *</span> }
			</label>
			{ input }
			{ help && (
				<p className="nb-text-xs nb-text-gray-500 nb-mt-1 nb-m-0">
					{ help }
				</p>
			) }
		</div>
	);
}
